import { ref, computed, nextTick } from "vue";
import type { Ref } from "vue";
import type { ListOptions } from "../types";
import { ApolloQueryResult, FetchResult } from "@apollo/client/core";
import {
  DeliveryRequestUpdateInput,
  OperationInput,
  MemberUpdateInput,
  UpdateDeliveryRequestDocument,
  UpdateDeliveryRequestMutation,
  UpdateDeliveryRequestMutationVariables,
  UpdateMemberMutation,
  UpdateMemberMutationVariables,
  UpdateMemberDocument,
  ListDeliveryRequestQuery,
  ListDeliveryRequestQueryVariables,
  ListDeliveryRequestDocument,
  DeliveryRequestListInput,
  DeliveryRequestCountInput,
} from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { generateApolloClient } from "@/common/apolloClientUtil";

interface UseMemberEditFormParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  editPage: Ref<boolean>;
  confirmPage: Ref<boolean>;
  overlay: Ref<boolean>;
  processFlag: Ref<number>;
  editMember: Ref<MemberUpdateInput | null>;
  openListPage: () => void;
  searchQuery: () => Promise<void>;
  wordList: Ref<Record<string, string>>;
  isWordListReady: Ref<boolean>;
  listOptions: ListOptions;
  nowDate: Ref<string>;
  statusSelectedItem: Ref<string[]>;
}

/**
 * 編集フォームの入力状態と値同期処理を提供する composable。
 *
 * @returns 画面フォームで利用する状態と操作関数
 */
export const useMemberEditForm = (params: UseMemberEditFormParams) => {
  const {
    errdialog,
    errMessage,
    editPage,
    confirmPage,
    overlay,
    processFlag,
    editMember,
    openListPage,
    searchQuery,
    wordList,
    isWordListReady,
    listOptions,
    nowDate,
    statusSelectedItem,
  } = params;

  const deliveryRequestFlag = ref(false);

  /**
   * 置換前の備考欄
   */
  const memberNotesBefore = ref("");
  const messageMemberNotes = ref("");
  const editMemberInitialValues = computed<Record<string, string>>(() => {
    if (!editMember.value || !isWordListReady.value) return {};

    return {
      [wordList.value.lbl_member_id]: editMember.value.member_id ?? "",
      [wordList.value.lbl_delivery_destination_address_detail]:
        editMember.value.address2 ?? "",
      [wordList.value.lbl_room_num]: editMember.value.address3 ?? "",
      [wordList.value.lbl_family_name_kanji]: editMember.value.name1 ?? "",
      [wordList.value.lbl_first_name_kanji]: editMember.value.name2 ?? "",
      [wordList.value.lbl_family_name_kana]: editMember.value.name_kana1 ?? "",
      [wordList.value.lbl_first_name_kana]: editMember.value.name_kana2 ?? "",
      [wordList.value.lbl_tel]: editMember.value.phone ?? "",
      [wordList.value.lbl_house_card]: editMember.value.house_card ?? "",
      [wordList.value.lbl_memo_member]: editMember.value.notes ?? "",
    };
  });

  //  会員更新 input
  const memberUpdateInput = ref<MemberUpdateInput>({
    customer_code: "",
    chain_code: "",
    address1: "",
    address2: "",
    address3: "",
    member_id: "",
    name1: "",
    name2: "",
    name_kana1: "",
    name_kana2: "",
    search_kana: "",
    notes: "",
    phone: "",
    shop_code: "",
    zip_code: "",
    house_card: "",
  });

  //  会員作成 logInput
  const operationInput = ref<OperationInput>({
    area_code: "10",
    detail: "操作詳細",
    display_category: "画面名",
    operation: "操作",
    user_id: localStorageGetUserId(),
  });

  // 会員作成 レスポンス
  const updateDeliveryRequestMutation = ref<UpdateDeliveryRequestMutation | null | undefined>(null);

  // 会員更新 レスポンス
  const updateMemberMutation = ref<UpdateMemberMutation | null | undefined>(null);

  // 配達依頼情報
  const deliveryRequestList = ref<ApolloQueryResult<ListDeliveryRequestQuery> | null | undefined>(null);

  /**
   * 会員編集 キャンセル @Click
   */
  const onClickUpdateCancel = async (): Promise<void> => {
    // 編集対象 会員
    editMember.value = null;

    // 編集ページ表示フラグ
    editPage.value = false;
  };

  /**
   * 会員編集 登録(確認) @Click
   */
  const onClickUpdate = async (): Promise<void> => {
    //処理フラグ
    processFlag.value = 1;

    // 編集ページ表示フラグ
    editPage.value = false;

    // 確認ページ表示フラグ
    confirmPage.value = true;

    // 配達依頼情報取得中はローディング表示
    overlay.value = true;

    // 配達依頼情報存在チェック
    deliveryRequestFlag.value = false;
    await getDeliveryRequestList();

    const list = deliveryRequestList.value?.data.listDeliveryRequest;
    if (Array.isArray(list) && list.length > 0) {
      deliveryRequestFlag.value = true;
    }

    // ローディング非表示
    overlay.value = false;

    // 会員備考のオリジナルを保持し、ダブルクォーテーションを削除
    if (editMember.value?.notes?.includes('"')) {
      memberNotesBefore.value = editMember.value.notes;
      editMember.value.notes = editMember.value.notes.replace(/"/g, "");
      messageMemberNotes.value =
        "(※会員備考のダブルクォーテーションは削除されています)";
    }
  };

  /**
   * 会員更新確認キャンセル @Click
   */
  const onClickConfirmUpdateCancel = async (): Promise<void> => {
    // 会員備考のダブルクォーテーションを戻す。
    if (memberNotesBefore.value != "") {
      if (editMember.value) {
        editMember.value.notes = memberNotesBefore.value;
        memberNotesBefore.value = "";
        messageMemberNotes.value = "";
      }
    }

    if (processFlag.value < 2) {
      // 編集ページ表示フラグ
      editPage.value = true;
    } else {
      editPage.value = false;
    }

    // 確認ページ表示フラグ
    confirmPage.value = false;
  };

  /**
   * 会員情報を更新する @Click
   */
  const onClickConfirmUpdateOk = async (): Promise<void> => {
    overlay.value = true;
    try {
      // 会員情報を更新する
      await updateMember();

      // 編集対象 会員
      editMember.value = null;
      // 一覧表示を表示する
      openListPage();
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "会員の編集に失敗しました。";
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    }

    await nextTick(async () => {
      // 絞り込み検索
      await searchQuery();
      overlay.value = false;
    });
  };

  /**
   * 会員情報と配達依頼を更新する @Click
   */
  const onClickConfirmUpdateAndDeliveryRequest = async (): Promise<void> => {
    overlay.value = true;
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 更新対象の配達依頼一覧取得
    try {
      const list = deliveryRequestList.value?.data.listDeliveryRequest;
      if (Array.isArray(list) && list.length > 0) {
        list.forEach(async (value) => {
          if (value !== undefined && value !== null) {
            // 配達依頼更新
            // 配達依頼更新 input
            const input: DeliveryRequestUpdateInput = {
              area_code: value.area_code,
              chain_code: localStorageGetChainCode(),
              customer_code: localStorageGetCustomerCode(),
              delivery_address1: editMember.value!.address1,
              delivery_address2: editMember.value!.address2,
              delivery_address3: editMember.value!.address3,
              delivery_id: value.delivery_id,
              delivery_name1: editMember.value!.name1,
              delivery_name2: editMember.value!.name2,
              delivery_name_kana1: editMember.value!.name_kana1,
              delivery_name_kana2: editMember.value!.name_kana2,
              delivery_notes: value.delivery_notes,
              delivery_phone: editMember.value!.phone,
              delivery_zip_code: editMember.value!.zip_code,
              member_id: Number(editMember.value!.member_id),
              member_notes: editMember.value!.notes,
              payment_amount: value.payment_amount,
              payment_method: String(value.payment_method),
              receipt_no: value.receipt_no,
              reception_no: value.reception_no,
              register_no: value.register_no,
              required_delivery_date: value.required_delivery_date,
              shop_code: localStorageGetShopCode(),
              unit: value.unit,
              irregular_delivery_start_time: value.irregular_delivery_start_time,
              irregular_delivery_end_time: value.irregular_delivery_end_time,
              house_card: editMember.value!.house_card,
            };
            // 配達依頼更新
            try {
              let result: FetchResult<UpdateDeliveryRequestMutation> =
                  await apolloClient.mutate<
                      UpdateDeliveryRequestMutation,
                      UpdateDeliveryRequestMutationVariables
                  >({
                    mutation: UpdateDeliveryRequestDocument,
                    variables: {
                      input: input,
                      logInput: operationInput.value,
                    },
                  });

              updateDeliveryRequestMutation.value = result.data;
            } catch (err: unknown) {
              console.error(err);
              overlay.value = false;
              errdialog.value = true;
              if (err instanceof Error) {
                errMessage.value = "配達依頼の更新に失敗しました。";
              } else {
                errMessage.value = "不明なエラーが発生しました。";
              }
              return;
            }
          }
        });
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "配達依頼一覧データの取得に失敗しました。";
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    }

    try {
      // 会員情報を更新する
      await updateMember();

      // 編集対象 会員
      editMember.value = null;
      // 一覧表示を表示する
      openListPage();
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "会員の編集に失敗しました。";
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
      return;
    }

    await nextTick(async () => {
      // 絞り込み検索
      await searchQuery();
      overlay.value = false;
    });
  };

  /**
   * 会員情報更新
   */
  const updateMember = async (): Promise<void> => {
    if (editMember.value === null) {
      return;
    }

    //  会員更新 input
    memberUpdateInput.value.customer_code = localStorageGetCustomerCode();
    memberUpdateInput.value.chain_code = localStorageGetChainCode();
    memberUpdateInput.value.address1 = editMember.value.address1;
    memberUpdateInput.value.address2 = editMember.value.address2;
    memberUpdateInput.value.address3 = editMember.value.address3;
    memberUpdateInput.value.member_id = editMember.value.member_id;
    memberUpdateInput.value.name1 = editMember.value.name1;
    memberUpdateInput.value.name2 = editMember.value.name2;
    memberUpdateInput.value.name_kana1 = editMember.value.name_kana1;
    memberUpdateInput.value.name_kana2 = editMember.value.name_kana2;
    memberUpdateInput.value.search_kana =
        editMember.value.name_kana1 + editMember.value.name_kana2;
    memberUpdateInput.value.notes = editMember.value.notes;
    memberUpdateInput.value.phone = editMember.value.phone;
    memberUpdateInput.value.shop_code = editMember.value.shop_code;
    memberUpdateInput.value.zip_code = editMember.value.zip_code;
    memberUpdateInput.value.house_card = editMember.value.house_card;

    //graphql接続
    const apolloClient = await generateApolloClient();

    const result: FetchResult<UpdateMemberMutation> = await apolloClient.mutate<
        UpdateMemberMutation,
        UpdateMemberMutationVariables
    >({
      mutation: UpdateMemberDocument,
      variables: {
        input: memberUpdateInput.value,
        logInput: operationInput.value,
      },
    });

    updateMemberMutation.value = result.data;
  };

  /**
   * 配達依頼情報取得
   */
  const getDeliveryRequestList = async (): Promise<void> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    const listInput: DeliveryRequestListInput = {
      member_id: Number(editMember.value!.member_id),
      delivery_date_from: nowDate.value,
      delivery_status: statusSelectedItem.value,
      chain_code: localStorageGetChainCode(),
      customer_code: localStorageGetCustomerCode(),
      limit: listOptions.itemsPerPage,
      offset: 0,
    };

    // 会員作成 logInput
    const operation: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    const countInput: DeliveryRequestCountInput = {
      delivery_date_from: nowDate.value,
      delivery_status: statusSelectedItem.value,
      chain_code: listInput.chain_code,
      customer_code: listInput.customer_code,
      user_id: localStorageGetUserId(),
    };

    // 更新対象の配達依頼一覧取得
    try {
      deliveryRequestList.value = await apolloClient.query<
          ListDeliveryRequestQuery,
          ListDeliveryRequestQueryVariables
      >({
        query: ListDeliveryRequestDocument,
        variables: {
          listInput,
          logInput: operation,
          countInput,
        },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = "配達依頼一覧データの取得に失敗しました。";
      } else {
        errMessage.value = "不明なエラーが発生しました。";
      }
    }
  };

  return {
    deliveryRequestFlag,
    memberNotesBefore,
    messageMemberNotes,
    editMemberInitialValues,
    onClickUpdateCancel,
    onClickUpdate,
    onClickConfirmUpdateCancel,
    onClickConfirmUpdateOk,
    onClickConfirmUpdateAndDeliveryRequest,
  };
};
